<div class="processes-page">
  <div class="page-header">
    <div class="page-header__title">
      <h2>{{ 'Process definitions' | translate }}</h2>
      <p>{{ 'Executable orchestration definitions registered by the backend' | translate }}</p>
      <p>{{ 'Every assignment publishes these events' | translate }}</p>
      <app-orchestration-process-events [events]="coreEvents()" [label]="''"></app-orchestration-process-events>
    </div>
    <div class="page-header__search">
      <app-orchestration-catalog-search [(query)]="searchQuery"></app-orchestration-catalog-search>
    </div>
    <div class="header-actions">
      <app-secondary-button title="FLOW_TEST_TITLE" icon="pi pi-share-alt" routerLink="/admin/process-management/process-flow-test"></app-secondary-button>
      <app-secondary-button
        title="Create assignment"
        icon="pi pi-plus"
        (action)="openAssignments()"
      ></app-secondary-button>
      <app-secondary-button
        title="Create subscription"
        icon="pi pi-calendar-plus"
        (action)="openSubscriptions()"
      ></app-secondary-button>
    </div>
  </div>

  <div class="catalog-order">
    @for (chip of catalogOrderChips(); track chip.id) {
      <button
        type="button"
        class="filter-chip"
        [class.active]="chip.selected"
        (click)="selectCatalogOrder(chip.id)"
      >
        <span>{{ chip.label | translate }}</span>
      </button>
    }
    <button
      type="button"
      class="filter-chip"
      [class.active]="missingFailSubscriber()"
      (click)="toggleMissingFailSubscriber()"
    >
      <span>{{ 'No fail subscriber' | translate }}</span>
    </button>
  </div>

  @if (errorMessage()) {
    <div class="error-message">{{ errorMessage() | translate }}</div>
  }

  @if (loading()) {
    <app-loading [loading]="true"></app-loading>
  } @else {
    @if (eventsFirst()) {
      @for (group of eventGroups(); track group.id) {
        <section class="process-group">
          <header class="process-group__header">
            <i [class]="group.icon" aria-hidden="true"></i>
            <h3>{{ group.labelKey | translate }}</h3>
            <span class="process-group__count">{{ group.processes.length }}</span>
          </header>
          <div class="event-grid">
            @for (event of group.processes; track event.key) {
              <article class="event-card">
                <a class="event-card__main" [routerLink]="event.url">
                  <i class="pi pi-bolt" aria-hidden="true"></i>
                  <div>
                    <h3>{{ event.displayName | translate }}</h3>
                    <span class="event-key">
                      <app-copyable-identifier [value]="event.key"></app-copyable-identifier>
                    </span>
                    @if (event.description) {
                      <p>{{ event.description | translate }}</p>
                    }
                  </div>
                </a>
                @if (event.triggers.length) {
                  <ul class="event-relations">
                    @for (trigger of event.triggers; track trigger.id) {
                      <li class="event-relation" [class.inactive]="!trigger.isActive">
                        <i class="pi pi-forward" aria-hidden="true"></i>
                        <div>
                          <strong>{{ trigger.name | translate }}</strong>
                          <span>
                            @if (trigger.sourceProcessUrl) {
                              <a [routerLink]="trigger.sourceProcessUrl">{{ trigger.sourceProcessLabel | translate }}</a>
                            } @else {
                              <span>{{ trigger.sourceProcessLabel | translate }}</span>
                            }
                            <i class="pi pi-arrow-right" aria-hidden="true"></i>
                            @if (trigger.targetProcessUrl) {
                              <a [routerLink]="trigger.targetProcessUrl">{{ trigger.targetProcessLabel | translate }}</a>
                            } @else {
                              <span>{{ trigger.targetProcessLabel | translate }}</span>
                            }
                          </span>
                        </div>
                      </li>
                    }
                  </ul>
                }
                <app-secondary-button
                  title="Notify people"
                  icon="pi pi-bell"
                  size="small"
                  (action)="notifyPeople(event.key)"
                ></app-secondary-button>
              </article>
            }
          </div>
        </section>
      }
    }

    @if (hasCatalogFlows()) {
      <p class="section-hint">{{ 'How a cron subscription or an event starts a process' | translate }}</p>
    }

    @for (group of moduleSections(); track group.id) {
      <section class="process-group">
        <header class="process-group__header">
          <i [class]="group.icon" aria-hidden="true"></i>
          <h3>{{ group.labelKey | translate }}</h3>
          <span class="process-group__count">{{ group.count }}</span>
        </header>
        @if (group.flows.length) {
          <div class="process-flows">
            @for (flow of group.flows; track flow.id) {
              <article class="process-flow" [class.process-flow--edges]="!flow.linear">
                @if (flow.linear) {
                  @for (step of flow.steps; track step.node.key) {
                    <ng-container *ngTemplateOutlet="flowProcessTemplate; context: { $implicit: step.node }"></ng-container>
                    @for (edge of step.outgoing; track edge.id) {
                      <ng-container *ngTemplateOutlet="flowEventArrowTemplate; context: { $implicit: edge }"></ng-container>
                    }
                  }
                } @else {
                  @for (edge of flow.edges; track edge.id) {
                    <div class="flow-row">
                      <ng-container *ngTemplateOutlet="flowProcessTemplate; context: { $implicit: edge.source }"></ng-container>
                      <ng-container *ngTemplateOutlet="flowEventArrowTemplate; context: { $implicit: edge }"></ng-container>
                      <ng-container *ngTemplateOutlet="flowProcessTemplate; context: { $implicit: edge.target, isTarget: true }"></ng-container>
                    </div>
                  }
                }
              </article>
            }
          </div>
        }
        @if (group.processes.length) {
          <div class="process-grid">
            @for (process of group.processes; track process.key) {
              <article class="process-card" [class.process-card--failed]="process.activity?.hasError === true">
                <a class="process-card__main" [routerLink]="['/admin/process-management/processes', process.key]">
                  <i class="pi pi-sitemap"></i>
                  <div>
                    <h3>{{ (process.displayName || process.key) | translate }}</h3>
                    @if (process.description) {
                      <p class="process-description">{{ process.description | translate }}</p>
                    }
                    <span class="process-key">
                      <app-copyable-identifier [value]="process.key"></app-copyable-identifier>
                    </span>
                    <span>{{ 'Version' | translate }} {{ process.version }}</span>
                    <app-process-admission-facts
                      [maxConcurrentAssignments]="process.maxConcurrentAssignments"
                      [tags]="process.requiredWorkerTags"
                    ></app-process-admission-facts>
                    @if (process.executionSuspended) {
                      <span class="process-stopped">{{ 'Process stopped' | translate }}</span>
                    }
                    @for (incoming of process.incomingAny; track incoming.eventKey) {
                      <span class="incoming-any" [class.inactive]="!incoming.isActive">
                        <i class="pi pi-bolt" aria-hidden="true"></i>
                        <span>
                          {{ 'Started by any process' | translate }}:
                          {{ incoming.eventLabel | translate }}
                          <app-copyable-identifier [value]="incoming.eventKey"></app-copyable-identifier>
                        </span>
                      </span>
                    }
                  </div>
                </a>
                <app-command-config-links [processKey]="process.key"></app-command-config-links>
                <app-orchestration-process-activity [activity]="process.activity ?? null"></app-orchestration-process-activity>
                <app-orchestration-process-events [events]="process.publishedEvents"></app-orchestration-process-events>
              </article>
            }
          </div>
        }
      </section>
    }

    @if (showEmptyState()) {
      <div class="empty-state">{{ emptyCatalogMessage() | translate }}</div>
    }
  }
</div>

<ng-template #flowProcessTemplate let-node let-isTarget="isTarget">
  <div class="flow-process">
    @if (node.subscriptions.length) {
      <div class="flow-starts">
        @for (subscription of node.subscriptions; track subscription.id) {
          <div class="flow-start" [class.inactive]="!subscription.isActive">
            <a class="flow-node flow-node--subscription" [routerLink]="subscription.url">
              <i class="pi pi-clock" aria-hidden="true"></i>
              <div>
                <h4>{{ subscription.name | translate }}</h4>
                <span>{{ 'Cron subscription' | translate }}</span>
                @if (!subscription.isActive) {
                  <span class="flow-inactive-label">{{ 'Inactive' | translate }}</span>
                }
                <strong>{{ subscription.cronExpression | quartzCronLabel }}</strong>
              </div>
            </a>
            <div class="flow-arrow flow-arrow--cron" [class.inactive]="!subscription.isActive">
              <span class="flow-arrow__event">
                <i class="pi pi-clock" aria-hidden="true"></i>
                <span>
                  {{ 'Started by cron subscription' | translate }}
                  <strong>{{ subscription.cronExpression | quartzCronLabel }}</strong>
                  @if (!subscription.isActive) {
                    <em>{{ 'Inactive' | translate }}</em>
                  }
                </span>
                <i class="pi pi-arrow-right" aria-hidden="true"></i>
              </span>
            </div>
          </div>
        }
      </div>
    }
    <div class="flow-process-body">
      <div class="flow-node flow-node--process" [class.process-card--failed]="node.activity?.hasError === true">
        <a class="flow-node__main" [routerLink]="node.url">
          <i class="pi" [class.pi-send]="isTarget" [class.pi-sitemap]="!isTarget"></i>
          <div>
            <h4>{{ node.label | translate }}</h4>
            <span class="process-key">
              <app-copyable-identifier [value]="node.key"></app-copyable-identifier>
            </span>
            <app-process-admission-facts
              [maxConcurrentAssignments]="node.maxConcurrentAssignments"
              [tags]="node.requiredWorkerTags"
            ></app-process-admission-facts>
            @if (node.executionSuspended) {
              <span class="process-stopped">{{ 'Process stopped' | translate }}</span>
            }
            @for (incoming of node.incomingAny; track incoming.eventKey) {
              <span class="incoming-any" [class.inactive]="!incoming.isActive">
                <i class="pi pi-bolt" aria-hidden="true"></i>
                <span>
                  {{ 'Started by any process' | translate }}:
                  {{ incoming.eventLabel | translate }}
                  <app-copyable-identifier [value]="incoming.eventKey"></app-copyable-identifier>
                </span>
              </span>
            }
          </div>
        </a>
        <app-orchestration-process-activity [activity]="node.activity ?? null"></app-orchestration-process-activity>
        <app-orchestration-process-events [events]="node.publishedEvents"></app-orchestration-process-events>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #flowEventArrowTemplate let-edge>
  <div class="flow-arrow" [class.inactive]="!edge.isActive">
    @if (edge.eventUrl) {
      <a class="flow-arrow__event" [routerLink]="edge.eventUrl">
        <i class="pi pi-bolt" aria-hidden="true"></i>
        <span>
          {{ edge.eventLabel | translate }}
          <app-copyable-identifier [value]="edge.eventKey"></app-copyable-identifier>
        </span>
      </a>
    } @else {
      <span class="flow-arrow__event">
        <i class="pi pi-bolt" aria-hidden="true"></i>
        <span>
          {{ edge.eventLabel | translate }}
          <app-copyable-identifier [value]="edge.eventKey"></app-copyable-identifier>
        </span>
      </span>
    }
    <i class="pi pi-arrow-right" aria-hidden="true"></i>
  </div>
</ng-template>
